import React, { useState } from 'react';
import { Link, Copy, Trash2, Plus, Minus } from 'lucide-react';

const AnchorTextGeneratorTool = () => {
  const [url, setUrl] = useState('');
  const [keyword, setKeyword] = useState('');
  const [variations, setVariations] = useState([]);
  const [copied, setCopied] = useState('');

  const generateAnchorTexts = () => {
    if (!keyword.trim()) return;

    const baseKeyword = keyword.trim();
    const keywordLower = baseKeyword.toLowerCase();
    
    const generated = [
      // Exact match
      { type: 'Exact Match', text: baseKeyword, priority: 'Use sparingly (5-10%)' },
      
      // Partial match
      { type: 'Partial Match', text: `Best ${keywordLower}`, priority: 'Good balance (20-30%)' },
      { type: 'Partial Match', text: `${baseKeyword} guide`, priority: 'Good balance (20-30%)' },
      { type: 'Partial Match', text: `${baseKeyword} tips`, priority: 'Good balance (20-30%)' },
      
      // Branded
      { type: 'Branded', text: url ? new URL(url).hostname.replace('www.', '') : 'YourBrand', priority: 'Recommended (30-40%)' },
      { type: 'Branded + Keyword', text: `${url ? new URL(url).hostname.replace('www.', '') : 'YourBrand'} ${keywordLower}`, priority: 'Recommended (10-20%)' },
      
      // Generic
      { type: 'Generic', text: 'Click here', priority: 'Minimal use (5%)' },
      { type: 'Generic', text: 'Learn more', priority: 'Minimal use (5%)' },
      { type: 'Generic', text: 'Read more', priority: 'Minimal use (5%)' },
      { type: 'Generic', text: 'Visit website', priority: 'Minimal use (5%)' },
      
      // Long-tail
      { type: 'Long-tail', text: `How to use ${keywordLower}`, priority: 'Natural (15-20%)' },
      { type: 'Long-tail', text: `${baseKeyword} for beginners`, priority: 'Natural (15-20%)' },
      { type: 'Long-tail', text: `Complete ${keywordLower} guide`, priority: 'Natural (15-20%)' },
      
      // Naked URL
      { type: 'Naked URL', text: url || 'https://example.com', priority: 'Natural (5-10%)' },
    ];

    setVariations(generated);
  };

  const generateHtml = (text) => {
    return `<a href="${url || 'https://example.com'}">${text}</a>`;
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const typeColors = {
    'Exact Match': 'text-red-400',
    'Partial Match': 'text-yellow-400',
    'Branded': 'text-green-400',
    'Branded + Keyword': 'text-green-400',
    'Generic': 'text-gray-400',
    'Long-tail': 'text-blue-400',
    'Naked URL': 'text-purple-400',
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Target URL</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/page"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Target Keyword</label>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g., SEO tools"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
      </div>

      <button
        onClick={generateAnchorTexts}
        disabled={!keyword.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Link size={18} />
        Generate Anchor Texts
      </button>

      {variations.length > 0 && (
        <div className="space-y-4">
          <div className="p-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-300 mb-2">SEO Best Practices</h3>
            <p className="text-sm text-gray-400">
              Maintain a natural anchor text distribution. Avoid over-optimization with exact match anchors.
              Mix different types for a healthy backlink profile.
            </p>
          </div>

          <div className="space-y-3">
            {variations.map((v, i) => (
              <div key={i} className="p-4 bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${typeColors[v.type]}`}>{v.type}</span>
                  <span className="text-xs text-gray-500">{v.priority}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white">{v.text}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(v.text, `text-${i}`)}
                      className={`p-1.5 rounded transition-colors ${
                        copied === `text-${i}` ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                      title="Copy text"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => copyToClipboard(generateHtml(v.text), `html-${i}`)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        copied === `html-${i}` ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                      title="Copy HTML"
                    >
                      HTML
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Recommended Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-green-400 font-medium">30-40%</div>
            <div className="text-gray-500">Branded</div>
          </div>
          <div>
            <div className="text-yellow-400 font-medium">20-30%</div>
            <div className="text-gray-500">Partial Match</div>
          </div>
          <div>
            <div className="text-blue-400 font-medium">15-20%</div>
            <div className="text-gray-500">Long-tail</div>
          </div>
          <div>
            <div className="text-red-400 font-medium">5-10%</div>
            <div className="text-gray-500">Exact Match</div>
          </div>
        </div>
      </div>

      <button
        onClick={() => { setUrl(''); setKeyword(''); setVariations([]); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default AnchorTextGeneratorTool;
